//#region Imports
import { useEffect, useState, useContext, Fragment, useCallback, memo } from "react";
import axios from "axios";
import { AuthContext } from "../../contexts/AuthContext";
import ChapterTextEditor from "../ChapterTextEditor/ChapterTextEditor";
import PaginationComponent from '../PaginationComponent';
import {
  Card,
  Alert,
  ListGroup,
  Button,
  Form,
  Row,
  Col,
  Badge,
  Stack,
  Accordion,
  Spinner,
} from "react-bootstrap";
import { API_ENDPOINTS } from "../../configs/apiConfig";
import { LanguageContext } from "../../contexts/LanguageProvider";
import { FaArrowUp, FaArrowDown, FaTrash } from "react-icons/fa";
import translations from "./translations";
import { useAlertContext } from '../../contexts/AlertContext';
import useMediaQuery from "../../utils/useMediaQuery"; // Import the custom hook
import ModalCustom from "../ModalCustom";
import BrandSelect from "../common/BrandSelect";
//#endregion

//#region Componente Principal
function BookEditing() {
  //#region Declarações
  const { username, role } = useContext(AuthContext);
  const { language } = useContext(LanguageContext);
  const [books, setBooks] = useState([]);
  const [newBookTitle, setNewBookTitle] = useState("");
  const [newChapterTitle, setNewChapterTitle] = useState("");
  const [newChapterOwnerId, setNewChapterOwnerId] = useState("");
  const [newChapterOnlyProducts, setNewChapterOnlyProducts] = useState(false);
  const [editors, setEditors] = useState([]);
  const [selectedBook, setSelectedBook] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [editChapterText, setEditChapterText] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [comments, setComments] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [viewTasks, setViewTasks] = useState(false);
  const [reorderingChapterId, setReorderingChapterId] = useState(null);

  // State for loading indicators
  const [loading, setLoading] = useState(true);
  const [addingBook, setAddingBook] = useState(false);
  const [addingChapter, setAddingChapter] = useState(false);
  const [updatingBook, setUpdatingBook] = useState(false);
  const [updatingChapter, setUpdatingChapter] = useState(false);
  const [toggleVisibility, setToggleVisibility] = useState(false);
  const [deletingChapter, setDeletingChapter] = useState(null);
  const [currentCommentsPage, setCurrentCommentsPage] = useState(1);
  const [commentsPerPage] = useState(5);
  const [totalCommentsPages, setTotalCommentsPages] = useState(1);
  const [totalComments, setTotalComments] = useState(0);
  const indexOfLastComment = currentCommentsPage * commentsPerPage;
  const indexOfFirstComment = indexOfLastComment - commentsPerPage;
  const [paginatedComments, setPaginatedComments] = useState([]);

  // Lógica de paginação de tasks
  const [currentTasksPage, setCurrentTasksPage] = useState(1);
  const [tasksPerPage, setTasksPerPage] = useState(3);
  const [totalTasksPages, setTotalTasksPages] = useState(2);
  const [totalTasks, setTotalTasks] = useState(0);
  const indexOfLastTask = currentTasksPage * tasksPerPage;
  const indexOfFirstTask = indexOfLastTask - tasksPerPage;
  const currentTasks = tasks.slice(indexOfFirstTask, indexOfLastTask);

  const [showInfoDeleteModal, setShowInfoDeleteModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { showSuccess, showError } = useAlertContext();

  // Constante para detectar dispositivos móveis
  const isMobile = useMediaQuery("(max-width: 768px)");

  const t = translations[language] || translations.pt;
  //#endregion

  //#region Efeitos para Paginação de Tasks
  useEffect(() => {
    setTasksPerPage(isMobile ? 5 : 8);
    setCurrentTasksPage(1); // Reseta para a primeira página ao mudar tasksPerPage
  }, [isMobile]);
  //#endregion

  //#region Função para download de arquivo Base64
  const handleDownload = useCallback((base64String, fileName) => {
    if (!base64String || !fileName) return;

    const link = document.createElement("a");
    let mimeType = "application/octet-stream";
    const parts = fileName.split(".");
    const extension = parts.length > 1 ? parts.pop().toLowerCase() : "";

    switch (extension) {
      case "png": mimeType = "image/png"; break;
      case "jpg":
      case "jpeg": mimeType = "image/jpeg"; break;
      case "gif": mimeType = "image/gif"; break;
      case "bmp": mimeType = "image/bmp"; break;
      case "webp": mimeType = "image/webp"; break;

      case "pdf": mimeType = "application/pdf"; break;
      case "txt": mimeType = "text/plain"; break;
      case "csv": mimeType = "text/csv"; break;
      case "json": mimeType = "application/json"; break;
      case "xml": mimeType = "application/xml"; break;
      case "md": mimeType = "text/markdown"; break;

      case "zip": mimeType = "application/zip"; break;
      case "rar": mimeType = "application/vnd.rar"; break;
      case "7z": mimeType = "application/x-7z-compressed"; break;

      default: mimeType = "application/octet-stream"; break;
    }

    link.href = `data:${mimeType};base64,${base64String}`;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);
  //#endregion

  //#region Efeitos (useEffect)
  const fetchData = useCallback(async (bookId) => {
    if (!username) {
      console.warn("Username is not available.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const auth = localStorage.getItem("auth");

      // Fetch current user ID
      const userResponse = await axios.get(
        API_ENDPOINTS.USERS_BY_USERNAME(username),
        {
          headers: { Authorization: `Basic ${auth}` },
        }
      );
      setCurrentUserId(userResponse.data.id);

      // Fetch books
      const booksResponse = await axios.get(API_ENDPOINTS.BOOKS_REFERENCES, {
        headers: { Authorization: `Basic ${auth}` },
      });
      const booksWithChapterPositions = booksResponse.data.map((book) => ({
        ...book,
        visible:
          book.visible !== null && book.visible !== undefined
            ? book.visible
            : true,
        chapters: book.chapters.map((chapter, index) => ({
          ...chapter,
          position: chapter.position,
        })),
      }));
      setBooks(booksWithChapterPositions);

      if (bookId) {
        setSelectedBook(booksWithChapterPositions.find((b) => b.id === bookId));
      }

      // Fetch editors
      const editorsResponse = await axios.get(API_ENDPOINTS.USERS_EDITORS, {
        headers: { Authorization: `Basic ${auth}` },
      });
      setEditors(editorsResponse.data);
    } catch (error) {
      showError(t.dataLoadError + error.message);
    } finally {
      setLoading(false);
    }
  }, [username, t.dataLoadError, showError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const fetchRelatedData = async () => {
      try {
        const auth = localStorage.getItem("auth");

        if (selectedBook) {
          const commentsResponse = await axios.get(
            API_ENDPOINTS.COMMENTS_BY_BOOK(selectedBook.id),
            { headers: { Authorization: `Basic ${auth}` } }
          );
          const sortedComments = commentsResponse.data.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
          setComments(sortedComments);

          // Filtrar comentários do capítulo selecionado e calcular paginação
          if (selectedChapter) {
            const chapterComments = sortedComments.filter(
              (c) => c.chapter.id === selectedChapter.id
            );
            setTotalComments(chapterComments.length);
            setTotalCommentsPages(Math.ceil(chapterComments.length / commentsPerPage));

            const startIndex = (currentCommentsPage - 1) * commentsPerPage;
            const endIndex = startIndex + commentsPerPage;
            setPaginatedComments(chapterComments.slice(startIndex, endIndex));
          }
        }

        if (selectedChapter) {
          const tasksResponse = await axios.get(
            API_ENDPOINTS.TASKS_BY_CHAPTER(selectedChapter.id),
            { headers: { Authorization: `Basic ${auth}` } }
          );
          setTasks(tasksResponse.data);
          setTotalTasks(tasksResponse.data.length);
          setTotalTasksPages(Math.ceil(tasksResponse.data.length / tasksPerPage));
          setCurrentTasksPage(1); // Reseta a página ao carregar novas tasks
        }
      } catch (error) {
        showError(t.relatedDataError + error.message);
      }
    };

    fetchRelatedData();
  }, [selectedBook, selectedChapter, currentCommentsPage, commentsPerPage, t.relatedDataError, showError, tasksPerPage]);

  // Função para mudar a página de comentários



  const handleCommentsPageChange = (pageNumber) => {
    setCurrentCommentsPage(pageNumber);
  };

  // Função para mudar a página de tasks
  const handleTasksPageChange = (pageNumber) => {
    setCurrentTasksPage(pageNumber);
  };
  //#region Funções Auxiliares
  const editableBooks = books.filter(
    (book) => book.editor?.id === currentUserId || role === "ADMIN"
  );

  const handleChapter = async (chapterID) => {
    if (!chapterID) return; // capítulo ainda não sincronizado (sem id) — evita /api/chapters/undefined
    try {
      const auth = localStorage.getItem("auth");
      const response = await axios.get(API_ENDPOINTS.CHAPTERS_BY_ID(chapterID), {
        headers: { Authorization: `Basic ${auth}` },
      });
      setSelectedChapter(response.data);
    } catch (error) {
      showError(t.chapterLoadError + error.message);
    }
  };

  const handleBookEditChange = useCallback((bookId, field, value) => {
    setBooks(prevBooks =>
      prevBooks.map((book) =>
        book.id === bookId ? { ...book, [field]: value } : book
      )
    );

    setSelectedBook(prev => {
      if (prev?.id === bookId) {
        return { ...prev, [field]: value };
      }
      return prev;
    });
  }, []);

  const handleChapterEditChange = useCallback((bookId, chapterId, field, value) => {
    setBooks(prevBooks =>
      prevBooks.map((book) => {
        if (book.id === bookId) {
          return {
            ...book,
            chapters: book.chapters.map((chapter) =>
              chapter.id === chapterId
                ? { ...chapter, [field]: value }
                : chapter
            ),
          };
        }
        return book;
      })
    );

    setSelectedChapter(prev => {
      if (prev?.id === chapterId) {
        return { ...prev, [field]: value };
      }
      return prev;
    });
  }, []);

  const handleChapterTextBack = (updatedChapter) => {
    setSelectedChapter(updatedChapter);
    setEditChapterText(false);
  };

  const resetSelection = () => {
    setSelectedBook(null);
    setSelectedChapter(null);
    setEditChapterText(false);
    setComments([]);
  };
  //#endregion

  //#region Manipulação de Livros
  const handleAddBook = async (event) => {
    event?.preventDefault();
    if (!newBookTitle.trim()) return;

    try {
      setAddingBook(true);
      const auth = localStorage.getItem("auth");
      const newBook = {
        title: newBookTitle,
        chapters: [],
        visible: true,
      };

      const response = await axios.post(API_ENDPOINTS.BOOKS, newBook, {
        headers: {
          Authorization: `Basic ${auth}`,
          "Content-Type": "application/json",
        },
      });

      setBooks([...books, response.data]);
      setNewBookTitle("");
      showSuccess(t.bookAddSuccess);
    } catch (error) {
      showError(t.bookAddError + error.message);
    } finally {
      setAddingBook(false);
    }
  };

  const handleUpdateBookTitle = async (bookId) => {
    try {
      setUpdatingBook(true);
      const auth = localStorage.getItem("auth");
      const updatedBook = books.find((book) => book.id === bookId);

      await axios.put(API_ENDPOINTS.BOOKS_BY_ID(bookId), updatedBook, {
        headers: {
          Authorization: `Basic ${auth}`,
          "Content-Type": "application/json",
        },
      });

      showSuccess(t.titleUpdateSuccess);
    } catch (error) {
      showError(t.titleUpdateError + t.defaultErrorMessage + error.message, 15000);
    } finally {
      setUpdatingBook(false);
    }
  };

  const handleToggleBookVisibility = async (bookId) => {
    try {
      setToggleVisibility(true);
      const auth = localStorage.getItem("auth");
      const book = books.find((b) => b.id === bookId);
      const updatedBook = {
        ...book,
        visible: !book.visible,
      };

      await axios.put(API_ENDPOINTS.BOOKS_BY_ID(bookId), updatedBook, {
        headers: {
          Authorization: `Basic ${auth}`,
          "Content-Type": "application/json",
        },
      });

      setBooks(books.map((b) => (b.id === bookId ? updatedBook : b)));
      setSelectedBook(updatedBook);
      showSuccess(updatedBook.visible ? t.bookPublished : t.bookHidden);
    } catch (error) {
      showError(t.visibilityUpdateError + t.defaultErrorMessage + error.message, 15000);
    } finally {
      setToggleVisibility(false);
    }
  };
  //#endregion

  //#region Manipulação de Capítulos
  const handleAddChapter = async (bookId) => {
    try {
      setAddingChapter(true);
      const auth = localStorage.getItem("auth");
      const updatedBook = books.find((book) => book.id === bookId);
      const newChapter = {
        title: newChapterTitle,
        content: null,
        owner: newChapterOwnerId ? { id: newChapterOwnerId } : null,
        visible: false,
        position: 0,
        onlyProducts: newChapterOnlyProducts ? true : false,
      };

      // Não mutar o estado: envia uma cópia do livro com o novo capítulo
      const bookPayload = { ...updatedBook, chapters: [...updatedBook.chapters, newChapter] };

      await axios.put(API_ENDPOINTS.BOOKS_BY_ID(bookId), bookPayload, {
        headers: {
          Authorization: `Basic ${auth}`,
          "Content-Type": "application/json",
        },
      });

      await fetchData(bookId);
      setNewChapterTitle("");
      setNewChapterOwnerId("");
      setNewChapterOnlyProducts(false);
      showSuccess(t.chapterAddSuccess);
    } catch (error) {
      showError(t.chapterAddError + t.defaultErrorMessage + error.message, 15000);
    } finally {
      setAddingChapter(false);
    }
  };

  const handleUpdateChapter = async (chapterId, updatedFields) => {
    try {
      setUpdatingChapter(true);
      const auth = localStorage.getItem("auth");

      const fieldsToUpdate = { ...updatedFields };
      if (fieldsToUpdate.content === undefined || fieldsToUpdate.content === null) {
        delete fieldsToUpdate.content;
      }

      await axios.put(
        API_ENDPOINTS.CHAPTERS_BY_ID(chapterId),
        fieldsToUpdate,
        {
          headers: {
            Authorization: `Basic ${auth}`,
            "Content-Type": "application/json",
          },
        }
      );

      const updatedBooks = books.map((book) => {
        const chapterIndex = book.chapters.findIndex((ch) => ch.id === chapterId);
        if (chapterIndex !== -1) {
          const updatedChapters = book.chapters.map((chapter) =>
            chapter.id === chapterId ? { ...chapter, ...updatedFields } : chapter
          );
          const updatedBook = { ...book, chapters: updatedChapters };

          if (selectedBook && selectedBook.id === book.id) {
            setSelectedBook(updatedBook);
          }

          return updatedBook;
        }
        return book;
      });

      setBooks(updatedBooks);

      if (selectedChapter && selectedChapter.id === chapterId) {
        setSelectedChapter({ ...selectedChapter, ...updatedFields });
      }

      showSuccess(t.chapterUpdateSuccess);
    } catch (error) {
      console.error("Erro ao atualizar chapter:", error);
      showError(t.chapterUpdateError + t.defaultErrorMessage + (error.response?.data?.message || error.message), 15000);
    } finally {
      setUpdatingChapter(false);
    }
  };

  const handleReorderChapter = (bookId, chapterId, direction) => {
    const auth = localStorage.getItem("auth");
    const book = books.find((b) => b.id === bookId);
    const chapterIndex = book.chapters.findIndex((c) => c.id === chapterId);

    if (direction === "up" && chapterIndex === 0) return;
    if (direction === "down" && chapterIndex === book.chapters.length - 1)
      return;

    setReorderingChapterId(chapterId);

    const updatedChapters = [...book.chapters];

    if (direction === "up") {
      updatedChapters[chapterIndex].position =
        updatedChapters[chapterIndex - 1].position;
      updatedChapters[chapterIndex - 1].position =
        updatedChapters[chapterIndex].position + 1;
    } else {
      updatedChapters[chapterIndex].position =
        updatedChapters[chapterIndex + 1].position;
      updatedChapters[chapterIndex + 1].position =
        updatedChapters[chapterIndex].position - 1;
    }

    updatedChapters.sort((a, b) => a.position - b.position);

    const updatedBook = {
      ...book,
      chapters: updatedChapters,
    };

    axios
      .put(API_ENDPOINTS.BOOKS_BY_ID(bookId), updatedBook, {
        headers: {
          Authorization: `Basic ${auth}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        setBooks(books.map((b) => (b.id === bookId ? updatedBook : b)));
        setSelectedBook(updatedBook);
        setReorderingChapterId(null);
        showSuccess(t.chaptersReordered);
      })
      .catch((error) => {
        setReorderingChapterId(null);
        showError(t.reorderError + t.defaultErrorMessage + error.message, 15000);
      });
  };

  const handleDeleteChapter = async (chapterId) => {
    try {
      const auth = localStorage.getItem("auth");

      await axios.delete(API_ENDPOINTS.CHAPTERS_BY_ID(chapterId), {
        headers: { Authorization: `Basic ${auth}` },
      });

      const updatedBooks = books.map((book) => {
        if (book.id === selectedBook.id) {
          const updatedChapters = book.chapters.filter((ch) => ch.id !== chapterId);
          return { ...book, chapters: updatedChapters };
        }
        return book;
      });

      setBooks(updatedBooks);
      const updatedSelectedBook = updatedBooks.find(b => b.id === selectedBook.id);
      setSelectedBook(updatedSelectedBook);

      if (selectedChapter && selectedChapter.id === chapterId) {
        setSelectedChapter(null);
        setEditChapterText(false);
        setViewTasks(false);
      }

      showSuccess(t.chapterDeleteSuccess);
    } catch (error) {
      showError(t.chapterDeleteError + error.message);
    } finally {
      setDeletingChapter(null);
    }
  };
  //#endregion

  //#region Componentes Memoizados
  const BookListItem = memo(({ book, onSelect, t }) => (
    <ListGroup.Item
      action
      onClick={() => onSelect(book)}
      className="d-flex justify-content-between align-items-center"
    >
      {book.title}
      <div className="ms-auto" style={{ display: "flex", gap: "10px" }}>
        <Badge bg="secondary">
          {book.chapters.length} {t.chapters}
        </Badge>
        <Badge bg={book.visible ? "success" : "danger"}>
          {book.visible ? t.visible : t.hidden}
        </Badge>
      </div>
    </ListGroup.Item>
  ));

  const ChapterListItem = memo(({
    chapter,
    index,
    totalChapters,
    onSelect,
    onReorder,
    onDelete,
    reorderingChapterId,
    deletingChapter,
    showDeleteInfo,
    t
  }) => (
    <div className="d-flex align-items-center mb-2">
      <div className="d-flex flex-row me-2">
        <Button
          variant="outline-secondary"
          size="sm"
          className="p-0 me-1"
          style={{ width: "28px", height: "28px" }}
          onClick={(e) => {
            e.stopPropagation();
            onReorder(chapter.id, "up");
          }}
          disabled={index === 0 || reorderingChapterId !== null}
        >
          {reorderingChapterId === chapter.id ? (
            <span className="spinner-border spinner-border-sm" />
          ) : (
            <FaArrowUp />
          )}
        </Button>
        <Button
          variant="outline-secondary"
          size="sm"
          className="p-0"
          style={{ width: "28px", height: "28px" }}
          onClick={(e) => {
            e.stopPropagation();
            onReorder(chapter.id, "down");
          }}
          disabled={index === totalChapters - 1 || reorderingChapterId !== null}
        >
          {reorderingChapterId === chapter.id ? (
            <span className="spinner-border spinner-border-sm" />
          ) : (
            <FaArrowDown />
          )}
        </Button>
      </div>
      <ListGroup.Item
        action
        onClick={() => onSelect(chapter.id)}
        className="d-flex justify-content-between align-items-center"
      >
        <div className="d-flex align-items-center">{chapter.title}</div>
        <div className="ms-auto" style={{ display: "flex", gap: "10px" }}>
          <Badge bg="secondary">
            {chapter.owner?.name || t.noOwner}
          </Badge>
          <Badge bg={chapter.visible ? "success" : "danger"}>
            {chapter.visible ? t.visible : t.hidden}
          </Badge>
        </div>
      </ListGroup.Item>
      {(chapter.title === 'delete' || chapter.title === '') ? (
        <button
          type="button"
          className="btn btn-outline-danger btn-sm ms-2"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(chapter.id);
          }}
          disabled={deletingChapter !== null}
          title={t.deleteChapter}
          style={{
            width: "28px",
            height: "28px",
            padding: "0",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {deletingChapter === chapter.id ? (
            <span className="spinner-border spinner-border-sm" />
          ) : (
            <FaTrash size={12} />
          )}
        </button>
      ) : (
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm ms-2"
          title={t.deleteChapter}
          style={{
            width: "28px",
            height: "28px",
            padding: "0",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={showDeleteInfo}
        >
          <FaTrash size={12} />
        </button>
      )}
    </div>
  ));
  //#endregion

  //#region Manipulação de Comentários
  const handleCommentAction = (commentId, newState) => {
    const auth = localStorage.getItem("auth");
    axios
      .put(API_ENDPOINTS.COMMENTS_BY_ID_STATE(commentId), newState, {
        headers: {
          Authorization: `Basic ${auth}`,
          "Content-Type": "application/json",
        },
      })
      .then(() => {
        setComments(comments.filter((c) => c.id !== commentId));
        showSuccess(
          newState === "DELETED_BOOK_OWNER"
            ? t.commentDeleted
            : t.commentForwarded
        );
      })
      .catch((error) => {
        showError(t.commentActionError + error.message);
      });
  };
  //#endregion

  //#region Renderização
  return (
    <div className="page-section">
      <Card>
        <Card.Header as="h2">
          <Stack direction="horizontal" gap={3}>
            <div>{t.editBooks}</div>
            {selectedBook && (
              <Button
                variant="outline-secondary align-self-right"
                size="sm"
                onClick={resetSelection}
                className="ms-auto"
              >
                {t.backToBooks}
              </Button>
            )}
          </Stack>
        </Card.Header>

        <Card.Body>
          {!selectedBook ? (
            <div className="book-management">
              <div className="border p-3 rounded mb-4">
                <h5>{t.addNewBook}</h5>
                <Form onSubmit={handleAddBook}>
                  <Row>
                    <Col md={9}>
                      <Form.Control
                        type="text"
                        value={newBookTitle}
                        onChange={(e) => setNewBookTitle(e.target.value)}
                        placeholder={t.bookTitlePlaceholder}
                        required
                      />
                    </Col>
                    <Col md={3}>
                      <Button
                        variant="primary"
                        className="w-100 mt-2 mt-md-0"
                        disabled={addingBook}
                        type="submit"
                      >
                        {addingBook ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            {t.adding}
                          </>
                        ) : (
                          t.addButton
                        )}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </div>

              <div className="border p-3 rounded">
                {loading ? (
                  <div className="text-center py-5 d-flex align-items-center justify-content-center">
                    <Spinner animation="border" role="status" className="me-2">
                      <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <span>{t.loadingData}</span>
                  </div>
                ) : (
                  <>
                    <h5 className="mb-3">{t.availableBooks}</h5>
                    <div className="d-none d-md-block">
                      <ListGroup>
                        {editableBooks.map((book) => (
                          <BookListItem
                            key={book.id}
                            book={book}
                            onSelect={setSelectedBook}
                            t={t}
                          />
                        ))}
                      </ListGroup>
                    </div>
                    <div className="d-md-none">
                      {editableBooks.map((book) => (
                        <Card key={book.id} className="mb-2" onClick={() => setSelectedBook(book)} style={{ cursor: 'pointer' }}>
                          <Card.Body className="p-2">
                            <div className="d-flex flex-column">
                              <div className="fw-bold mb-1">{book.title}</div>
                              <div className="d-flex gap-2 p-0">
                                <Badge bg="secondary">
                                  {book.chapters.length} {t.chapters}
                                </Badge>
                                <Badge bg={book.visible ? "success" : "danger"}>
                                  {book.visible ? t.visible : t.hidden}
                                </Badge>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : (
            <>
              {!selectedChapter && (
                <div className="border p-3 rounded mb-4">
                  <h4>
                    {t.editing} {selectedBook.title}
                  </h4>
                  <Form onSubmit={(e) => {
                    e.preventDefault();
                    handleUpdateBookTitle(selectedBook.id);
                  }}>

                    <Row className="g-1 mb-4">
                      <Col md={9}>
                        <Form.Control
                          value={selectedBook.title}
                          onChange={(e) =>
                            handleBookEditChange(
                              selectedBook.id,
                              "title",
                              e.target.value
                            )
                          }
                          required
                        />
                      </Col>
                      <Col md={3}>
                        <Button
                          type="submit"
                          variant="success"
                          className="w-100"
                          disabled={updatingBook}
                        >
                          {updatingBook ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              {t.saving}
                            </>
                          ) : (
                            t.saveTitle
                          )}
                        </Button>
                        <Button
                          type="submit"
                          variant={selectedBook.visible ? "secondary" : "primary"}
                          onClick={() => handleToggleBookVisibility(selectedBook.id)}
                          className="w-100 mt-2"
                          disabled={toggleVisibility}
                        >
                          {toggleVisibility ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              {t.updating}
                            </>
                          ) : (
                            selectedBook.visible ? t.hideBook : t.publishBook
                          )}
                        </Button>
                      </Col>
                    </Row>
                  </Form>

                  <div className="border p-3 rounded mb-4">
                    <h5>{t.addChapter}</h5>
                    <Form onSubmit={(e) => {
                      e.preventDefault();
                      handleAddChapter(selectedBook.id);
                    }}>

                      <Row className="g-1">
                        <Col md={12}>
                          <Form.Control
                            value={newChapterTitle}
                            onChange={(e) => setNewChapterTitle(e.target.value)}
                            placeholder={t.chapterTitlePlaceholder}
                            required
                          />
                        </Col>
                        <Col md={6} className="mt-2">
                          <BrandSelect
                            className="w-100"
                            value={newChapterOwnerId}
                            onChange={(v) => setNewChapterOwnerId(v)}
                            placeholder={t.defaultOwner}
                            options={[
                              { value: "", label: t.defaultOwner },
                              ...editors.map((editor) => ({ value: editor.id, label: editor.name })),
                            ]}
                          />
                        </Col>
                        {/* <Col md={4} className="mt-2">
                          <Form.Check
                            type="checkbox"
                            label={t.onlyProducts}
                            checked={newChapterOnlyProducts}
                            onChange={(e) => setNewChapterOnlyProducts(e.target.checked)}
                            className="h-100 d-flex align-items-center"
                          />
                        </Col> */}
                        <Col md={2} className="mt-2">
                          <Button
                            type="submit"
                            variant="primary"
                            className="w-100"
                            disabled={addingChapter}
                          >
                            {addingChapter ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                {t.creating}
                              </>
                            ) : (
                              t.create
                            )}
                          </Button>
                        </Col>
                      </Row>
                    </Form>
                  </div>

                  <div className="border p-3 rounded">
                    <h5>{t.existingChapters}</h5>
                    <div className="d-none d-md-block">
                      <ListGroup>
                        {selectedBook.chapters
                          .sort((a, b) => a.position - b.position)
                          .map((chapter, index) => (
                            <ChapterListItem
                              key={chapter.id}
                              chapter={chapter}
                              index={index}
                              totalChapters={selectedBook.chapters.length}
                              onSelect={handleChapter}
                              onReorder={(chapterId, direction) =>
                                handleReorderChapter(selectedBook.id, chapterId, direction)
                              }
                              onDelete={(chapterId) => {
                                setDeletingChapter(chapterId);
                                setShowDeleteModal(true);
                              }}
                              reorderingChapterId={reorderingChapterId}
                              deletingChapter={deletingChapter}
                              showDeleteInfo={() => setShowInfoDeleteModal(true)}
                              t={t}
                            />
                          ))}
                      </ListGroup>
                      <ModalCustom
                        show={showInfoDeleteModal}
                        onHide={() => setShowInfoDeleteModal(false)}
                        title={t.deleteChapter}
                        message={t.deleteAlert}
                        close={t.understood}
                        isAction={false}
                      />
                      <ModalCustom
                        show={showDeleteModal}
                        onHide={() => {
                          setShowDeleteModal(false);
                          setDeletingChapter(null);
                        }}
                        title={t.deleteChapter}
                        message={t.deleteMessage}
                        close={t.cancel}
                        isAction={true}
                        onAction={() => {
                          handleDeleteChapter(deletingChapter);
                          setShowDeleteModal(false);
                        }}
                        textAction={t.delete}
                      />
                    </div>
                    <div className="d-md-none">
                      {selectedBook.chapters
                        .sort((a, b) => a.position - b.position)
                        .map((chapter, index) => (
                          <Card key={chapter.id} className="mb-2">
                            <Card.Body className="p-2">
                              <div className="d-flex justify-content-between align-items-start">
                                <div className="flex-grow-1" onClick={() => handleChapter(chapter.id)} style={{ cursor: 'pointer' }}>
                                  <div className="fw-bold mb-1">{chapter.title}</div>
                                  <div className="d-flex flex-wrap gap-1 mb-2">
                                    <Badge bg="secondary">
                                      {chapter.owner?.name || t.noOwner}
                                    </Badge>
                                    <Badge bg={chapter.visible ? "success" : "danger"}>
                                      {chapter.visible ? t.visible : t.hidden}
                                    </Badge>
                                  </div>
                                </div>
                                <div className="d-flex flex-column gap-1 ms-2">
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    className="p-1"
                                    style={{ width: "32px", height: "28px" }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleReorderChapter(selectedBook.id, chapter.id, "up");
                                    }}
                                    disabled={index === 0 || reorderingChapterId !== null}
                                  >
                                    {reorderingChapterId === chapter.id ? (
                                      <span className="spinner-border spinner-border-sm" />
                                    ) : (
                                      <FaArrowUp size={10} />
                                    )}
                                  </Button>
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    className="p-1"
                                    style={{ width: "32px", height: "28px" }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleReorderChapter(selectedBook.id, chapter.id, "down");
                                    }}
                                    disabled={index === selectedBook.chapters.length - 1 || reorderingChapterId !== null}
                                  >
                                    {reorderingChapterId === chapter.id ? (
                                      <span className="spinner-border spinner-border-sm" />
                                    ) : (
                                      <FaArrowDown size={10} />
                                    )}
                                  </Button>
                                  {(chapter.title === 'delete' || chapter.title === '') ? (
                                    <button
                                      type="button"
                                      className="btn btn-outline-danger btn-sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteChapter(chapter.id);
                                      }}
                                      disabled={deletingChapter !== null}
                                      style={{ width: "32px", height: "28px", padding: "0" }}
                                    >
                                      {deletingChapter === chapter.id ? (
                                        <span className="spinner-border spinner-border-sm" />
                                      ) : (
                                        <FaTrash size={10} />
                                      )}
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      className="btn btn-outline-secondary btn-sm"
                                      style={{ width: "32px", height: "28px", padding: "0" }}
                                      onClick={(e) => {
                                        window.alert(t.deleteAlert);
                                      }}
                                    >
                                      <FaTrash size={10} />
                                    </button>
                                  )}
                                </div>
                              </div>
                            </Card.Body>
                          </Card>
                        ))}
                    </div>
                    {reorderingChapterId && (
                      <Alert variant="info">
                        {t.reorderingChapters}
                      </Alert>
                    )}
                  </div>
                </div>
              )}

              {selectedChapter && !editChapterText && !viewTasks && (
                <div className="chapter-editor">
                  <div className="border p-3 rounded mb-4">
                    <Stack direction="horizontal" gap={3} className="mb-3">
                      <h5>
                        {t.editing} {selectedChapter.title}
                      </h5>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => {
                          setSelectedChapter(null);
                        }}
                        className="ms-auto"
                      >
                        {t.backToChapters}
                      </Button>
                    </Stack>

                    <Row className="g-1 mb-4">
                      <Col md={8}>
                        <Form.Label>{t.chapterTitle} :</Form.Label>
                        <Form.Control
                          value={selectedChapter.title}
                          onChange={(e) =>
                            handleChapterEditChange(
                              selectedBook.id,
                              selectedChapter.id,
                              "title",
                              e.target.value
                            )
                          }
                        />
                      </Col>
                      <Col md={4}>
                        <Form.Label>{t.chapterOwner} :</Form.Label>
                        <BrandSelect
                          className="w-100"
                          value={selectedChapter.owner?.id || ""}
                          onChange={(v) =>
                            handleChapterEditChange(
                              selectedBook.id,
                              selectedChapter.id,
                              "owner",
                              v ? { id: v } : null
                            )
                          }
                          placeholder={t.chapterOwner}
                          options={[
                            { value: "", label: t.chapterOwner },
                            ...editors.map((editor) => ({ value: editor.id, label: editor.name })),
                          ]}
                        />
                      </Col>
                    </Row>

                    {/* <Row className="g-1 mb-4">
	                      <Col md={12}>
	                        <Form.Check
	                          type="checkbox"
	                          label={t.onlyProducts}
	                          checked={selectedChapter.onlyProducts || false}
	                          onChange={(e) =>
	                            handleChapterEditChange(
	                              selectedBook.id,
	                              selectedChapter.id,
	                              "onlyProducts",
	                              e.target.checked ? true : false
	                            )
	                          }
	                        />
	                      </Col>
	                    </Row> */}

                    <Row className="g-1 mb-4">
                      <Col>
                        <Form.Label>{t.context} :</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={selectedChapter.context}
                          placeholder={t.contextPlaceholder}
                          onChange={(e) =>
                            handleChapterEditChange(
                              selectedBook.id,
                              selectedChapter.id,
                              "context",
                              e.target.value
                            )
                          }
                        />
                      </Col>
                    </Row>

                    <Stack className="w-100 d-flex flex-column flex-md-row gap-3">
                      <Button
                        variant="primary"
                        onClick={() =>
                          handleUpdateChapter(
                            selectedChapter.id,
                            {
                              title: selectedChapter.title,
                              owner: selectedChapter.owner || null,
                              context: selectedChapter.context,
                              tags: selectedChapter.tags || null,
                              onlyProducts: selectedChapter.onlyProducts || false
                            }
                          )
                        }
                        disabled={updatingChapter}
                      >
                        {updatingChapter ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            {t.saving}
                          </>
                        ) : (
                          t.saveChanges
                        )}
                      </Button>
                      {/* <Button
                        variant={
                          selectedChapter.visible ? "success" : "secondary"
                        }
                        onClick={() =>
                          handleUpdateChapter(
                            selectedChapter.id,
                            {
                              title: selectedChapter.title,
                              visible: !selectedChapter.visible,
                              context: selectedChapter.context,
                              tags: selectedChapter.tags || null,
                              onlyProducts: selectedChapter.onlyProducts || false
                            }
                          )
                        }
                      >
                        {selectedChapter.visible
                          ? t.hideChapter
                          : t.publishChapter}
                      </Button> */}
                      <Button
                        variant="outline-primary"
                        onClick={() => {
                          handleChapter(selectedChapter.id);
                          setEditChapterText(true);
                        }}
                      >
                        {t.editContent}
                      </Button>
                      <Button
                        variant="outline-info"
                        onClick={() => setViewTasks(true)}
                      >
                        {t.viewTasks}
                      </Button>
                    </Stack>
                  </div>

                  <div className="border p-3 rounded">
                    <h5 className="mb-3">{t.suggestions}</h5>
                    {/* Desktop version */}
                    <div className="d-none d-md-block">
                      <ListGroup variant="flush">
                        {paginatedComments
                          .filter((c) => c.chapter.id === selectedChapter.id)
                          .map((comment) => (
                            <ListGroup.Item key={comment.id}>
                              <div className="d-flex justify-content-between align-items-start">
                                <div>
                                  <div className="fw-bold">
                                    {comment.creator.name}
                                  </div>
                                  <div>
                                    {comment.text}
                                    <div className="d-flex gap-2 mt-1">
                                      {comment.pageNumber && (
                                        <Badge bg="secondary" className="align-content-center">
                                          {t.pageNumber}{comment.pageNumber}
                                        </Badge>
                                      )}
                                      {comment.sugestionType && (
                                        <Badge bg="info" pill="true" className="align-content-center">
                                          {comment.sugestionType}
                                        </Badge>
                                      )}
                                      {comment.imageBase64 && comment.nameImageBase64 && (
                                        <Button
                                          variant="outline-primary"
                                          size="sm"
                                          onClick={() => handleDownload(comment.imageBase64, comment.nameImageBase64)}
                                        >
                                          Download: {comment.nameImageBase64}
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                  <small className="text-muted">
                                    {new Date(comment.createdAt).toLocaleString()}
                                  </small>
                                </div>
                                <Stack gap={2} direction="horizontal">
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() =>
                                      handleCommentAction(
                                        comment.id,
                                        "DELETED_BOOK_OWNER"
                                      )
                                    }
                                  >
                                    {t.delete}
                                  </Button>
                                </Stack>
                              </div>
                            </ListGroup.Item>
                          ))}
                      </ListGroup>
                      <PaginationComponent
                        totalPages={totalCommentsPages}
                        currentPage={currentCommentsPage}
                        setCurrentPage={handleCommentsPageChange}
                        t={t}
                        indexOfFirst={indexOfFirstComment}
                        indexOfLast={indexOfLastComment}
                        lengthTotal={totalComments}
                        tItemName={t.commentsLow}
                        isMobile={false}
                      />
                    </div>
                    {/* Mobile version */}
                    <div className="d-md-none">
                      {paginatedComments
                        .filter((c) => c.chapter.id === selectedChapter.id)
                        .map((comment) => (
                          <Card key={comment.id} className="mb-2">
                            <Card.Body className="py-2">
                              <div className="mb-2">
                                <div className="fw-bold">{comment.creator.name}</div>
                                <div className="mb-1">{comment.text}</div>
                                <div className="d-flex flex-wrap gap-1 mb-2">
                                  {comment.pageNumber && (
                                    <Badge bg="secondary" className="align-content-center">
                                      {t.pageNumber}{comment.pageNumber}
                                    </Badge>
                                  )}
                                  {comment.sugestionType && (
                                    <Badge bg="info" pill="true" className="align-content-center">{comment.sugestionType}</Badge>
                                  )}
                                  {comment.imageBase64 && comment.nameImageBase64 && (
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() => handleDownload(comment.imageBase64, comment.nameImageBase64)}
                                    >
                                      Download: {comment.nameImageBase64}
                                    </Button>
                                  )}
                                </div>
                                <small className="text-muted">
                                  {new Date(comment.createdAt).toLocaleString()}
                                </small>
                              </div>
                              <div className="d-flex gap-2">
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleCommentAction(comment.id, "DELETED_BOOK_OWNER")}
                                >
                                  {t.delete}
                                </Button>
                                <Button
                                  variant="outline-warning"
                                  size="sm"
                                  onClick={() => handleCommentAction(comment.id, "CHAPTER_OWNER")}
                                >
                                  {t.forward}
                                </Button>
                              </div>
                            </Card.Body>
                          </Card>
                        ))}

                      <PaginationComponent
                        totalPages={totalCommentsPages}
                        currentPage={currentCommentsPage}
                        setCurrentPage={handleCommentsPageChange}
                        t={t}
                        indexOfFirst={indexOfFirstComment}
                        indexOfLast={indexOfLastComment}
                        lengthTotal={totalComments}
                        tItemName={t.commentsLow}
                        isMobile={true}
                      />
                    </div>
                  </div>
                </div>
              )}

              {selectedChapter && viewTasks && (
                <div className="task-management">
                  <div className="border p-3 rounded">
                    <Stack direction="horizontal" gap={3} className="mb-3">
                      <h5>
                        {t.tasksFor}
                        {selectedChapter.title}"
                      </h5>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => {
                          setViewTasks(false);
                        }}
                        className="ms-auto"
                      >
                        {t.backToChapterEdit}
                      </Button>
                    </Stack>

                    <Accordion defaultActiveKey="0">
                      {currentTasks.length > 0 ? (
                        currentTasks.map((task) => (
                          <Accordion.Item key={task.id} eventKey={task.id}>
                            <Accordion.Header>
                              <Badge
                                bg={task.state === "CLOSED" ? "success" : "warning"}
                                className={`me-2`}
                              >
                                #{task.referenceNumber}
                              </Badge>
                              {task.responsible.name} - {task.state}
                            </Accordion.Header>
                            <Accordion.Body>
                              <div className="mb-3">
                                <p>
                                  <strong>{t.instructions}</strong>{" "}
                                  {task.instructions || t.none}
                                </p>
                                {task.comments.length > 0 && (
                                  <>
                                    <h6>{t.comments}</h6>
                                    <ListGroup>
                                      {task.comments.map((comment) => (
                                        <ListGroup.Item key={comment.id}>
                                          <div>{comment.text}</div>
                                          <div className="d-flex gap-2 mt-1">
                                            {comment.pageNumber && (
                                              <Badge bg="secondary" className="align-content-center">
                                                {t.pageNumber}{comment.pageNumber}
                                              </Badge>
                                            )}
                                            {comment.sugestionType && (
                                              <Badge bg="info" pill="true" className="align-content-center">
                                                {comment.sugestionType}
                                              </Badge>
                                            )}
                                            {comment.imageBase64 && comment.nameImageBase64 && (
                                              <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => handleDownload(comment.imageBase64, comment.nameImageBase64)}
                                              >
                                                Download: {comment.nameImageBase64}
                                              </Button>
                                            )}
                                          </div>
                                          <small className="text-muted d-block mt-1">
                                            {t.by} {comment.creator.name} -{" "}
                                            {new Date(comment.createdAt).toLocaleString()}
                                          </small>
                                        </ListGroup.Item>
                                      ))}
                                    </ListGroup>
                                  </>
                                )}
                              </div>
                            </Accordion.Body>
                          </Accordion.Item>
                        ))
                      ) : (
                        <Alert variant="info" className="text-center">
                          {t.noTasksFound}
                        </Alert>
                      )}
                    </Accordion>

                    {totalTasksPages > 0 && (
                      <PaginationComponent
                        totalPages={totalTasksPages}
                        isMobile={isMobile}
                        setCurrentPage={handleTasksPageChange}
                        currentPage={currentTasksPage}
                        t={t}
                        tItemName={t.tasksLow}
                        indexOfFirst={indexOfFirstTask}
                        indexOfLast={indexOfLastTask}
                        lengthTotal={totalTasks}
                      />
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {selectedChapter && editChapterText && (
            <ChapterTextEditor
              entity={selectedChapter}
              onBack={handleChapterTextBack}
            />
          )}
        </Card.Body>
      </Card>
    </div>
  );
  //#endregion
}

export default BookEditing;